/**
 * Parameterization types — mirror cx-alm DTOs (V109 schema, 1.7.x backend).
 *
 * Source of truth (server side):
 *   services/cx-alm/.../presentation/dto/SegmentationResponse.java
 *   services/cx-alm/.../presentation/dto/FlowModelResponse.java
 *   services/cx-alm/.../presentation/dto/AffectationResponse.java
 */

export type ParamStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'ACTIVE' | 'ARCHIVED';

/** All gap types declared by the spec (CUBIX_DataModel.xlsx > Flow Model Affectation). */
export type GapType =
  | 'LIQUIDITY'
  | 'INTEREST_RATE'
  | 'LCR'
  | 'NSFR'
  | 'IRRBB'
  | 'FTP'
  | 'NIM';

export const ALL_GAP_TYPES: GapType[] = [
  'LIQUIDITY', 'INTEREST_RATE', 'LCR', 'NSFR', 'IRRBB', 'FTP', 'NIM',
];

// ── Segmentations ───────────────────────────────────────────────────

export interface Segmentation {
  id: string;
  tenantId: string;
  segmentId: number;
  segmentName: string;
  accountType?: 'BalanceSheet' | 'OffBalance' | string;
  accountNature?: 'Asset' | 'Liability' | string;
  products?: string;
  accountReference?: string;
  accountingChapterId?: string;
  rateTypeId?: string;
  currencyId?: string;
  priority: number;
  status: ParamStatus;
  notes?: string;
  createdAt: string;
  createdBy: string;
  approvedAt?: string;
  approvedBy?: string;
}

export interface SegmentationRequest {
  segmentId: number;
  segmentName: string;
  accountType?: string;
  accountNature?: string;
  products?: string;
  accountReference?: string;
  accountingChapterId?: string;
  rateTypeId?: string;
  currencyId?: string;
  priority: number;
  notes?: string;
}

// ── Flow Models ─────────────────────────────────────────────────────

export interface FlowModelStep {
  stepIndex: number;
  periodLabel: string;
  nbDays: number;
  valuePct: number;       // fraction; 0.025833 = 2.5833%
  notes?: string;
}

export interface FlowModel {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  description?: string;
  horizonDays?: number;
  totalPeriods?: number;
  status: ParamStatus;
  createdAt: string;
  createdBy: string;
  approvedAt?: string;
  approvedBy?: string;
  /** Server-computed sum of value_pct across steps. */
  sumValuePct?: number;
  steps: FlowModelStep[];
}

export interface FlowModelRequest {
  code: string;
  name: string;
  description?: string;
  steps: FlowModelStep[];
}

// ── Affectations ────────────────────────────────────────────────────

export interface Affectation {
  id: string;
  tenantId: string;
  segmentRowId: string;
  segmentId: number;
  segmentName: string;
  gapType: GapType;
  flowModelRowId: string;
  flowModelCode: string;
  flowModelName: string;
  horizonLabel?: string;
  notes?: string;
  status: ParamStatus;
  createdAt: string;
  createdBy: string;
  approvedAt?: string;
  approvedBy?: string;
}

export interface AffectationRequest {
  segmentRowId: string;
  gapType: GapType;
  flowModelRowId: string;
  horizonLabel?: string;
  notes?: string;
}
